import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Hub, WaterDamage, ElectricBolt, TravelExplore, CheckCircleOutline } from '@mui/icons-material';

// A styled component for assumption lists to reduce repetition
const AssumptionItem = ({ children }) => (
  <ListItem sx={{ alignItems: 'flex-start' }}>
    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
      <CheckCircleOutline fontSize="small" color="primary" />
    </ListItemIcon>
    <ListItemText secondary={children} />
  </ListItem>
);

export default function AboutTab() {
  return (
    <Paper sx={{ p: { xs: 2, sm: 4 }, maxWidth: '1200px', mx: 'auto', mt: 2, overflow: 'hidden' }}>
      <Typography component="h1" variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
        About the CityWise Infrastructure Dashboard
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        Our Mission
      </Typography>
      <Typography paragraph>
        This tool is engineered to provide a dynamic and scientifically-grounded assessment of risks to critical urban infrastructure. By simulating how disruptions cascade through interconnected systems, we aim to empower decision-makers to enhance urban resilience, prioritize protective measures, and safeguard communities.
      </Typography>

      {/* --- NEW SECTION: Digital Twin --- */}
      <Typography variant="h6" component="h2" sx={{ mt: 4, mb: 2 }}>
        Modeling the City's Digital Twin
      </Typography>
      <Typography paragraph>
        The backbone of our model is the city's road network, which acts as the primary conduit for the movement of goods and services. The synthetic networks are generated based on available open-source datasets. To ensure our simulation was realistic, we performed data cleaning to remove isolated road segments, creating a single, fully connected transportation grid. It is an underlying assumption that the pipelines and cables for water and electricity distribution follow the transportation network.
      </Typography>
      <Typography paragraph>
        To solve the challenge of determining how many people rely on each part of the road network, we used a geospatial allocation method. For every road intersection in our model, we calculated its local service area. We then calculated the population within each zone and assigned that number as the residential demand to the corresponding node. Therefore, the road nodes represent residential demands in the model. This technique translates population density into a direct load on the infrastructure network.
      </Typography>
      <Typography paragraph>
        For commercial, industrial, and governmental facilities, demand for services like water and electricity was calculated based on their type and function, using average standard figures. For each system, the total allocated demand was split equally between all supply nodes to guarantee that the total demand equals the total supply. All demand and supply units have been normalized as per capita demand units, adopting 4 MWh/year and 51 m³/year per capita residential demands of electricity and water, respectively.
      </Typography>

      {/* --- UPDATED SECTION: Cascading Failures with Full-Width Visual --- */}
      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        Core Concept: Cascading Failures
      </Typography>
      <Typography paragraph sx={{ mt: 2 }}>
        Our analysis engine simulates how a failure in one system can trigger a domino effect across others. For instance, a power substation failure can disable water pumps, impacting the availability of water for hospitals and fire stations. The diagram below illustrates the critical interdependencies between the systems we model.
      </Typography>
      
      {/* --- FULL WIDTH IMAGE --- */}
      <Box
        component="img"
        src="/interdependency_diagram.svg"
        alt="Infrastructure Interdependency Diagram"
        sx={{
          width: '100%',
          mt: 2,
          mb: 3,
          backgroundColor: 'transparent', // Explicitly ensure transparent background
        }}
      />

      {/* --- ANALYSIS PIPELINE --- */}
      <Typography variant="h6" component="h2" sx={{ mt: 4, mb: 2 }}>
        How The Analysis Works
      </Typography>
      <Box sx={{ pl: 2 }}>
        <Typography variant="body1"><strong>Step 1: Hazard Identification</strong></Typography>
        <Typography paragraph>The analysis begins by identifying which infrastructure components are directly exposed to a hazard, using either real-time flood warnings or user-defined scenarios.</Typography>

        <Typography variant="body1" sx={{ mt: 2 }}><strong>Step 2: System-Specific Simulation</strong></Typography>
        <Typography paragraph>To ensure accuracy, we employ specialized, state-of-the-art simulation models for each critical domain.</Typography>

        <Typography variant="body1" sx={{ mt: 2 }}><strong>Step 3: Cascading Impact Analysis</strong></Typography>
        <Typography paragraph>The models are integrated to create a realistic, multi-system dependency chain. The output from one simulation (e.g., a power outage) becomes a disruptive input for the next (e.g., disabling water pumps).</Typography>
      </Box>

      {/* --- ASSUMPTIONS & LIMITATIONS --- */}
      <Typography variant="h6" component="h2" sx={{ mt: 4, mb: 2 }}>
        Core Assumptions & Limitations
      </Typography>
      <Typography paragraph>
        Understanding the model's assumptions is key to interpreting the results correctly. Below are the foundational principles and limitations for each simulation model.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Power Model Assumptions */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <ElectricBolt color="secondary" />
              <Typography variant="subtitle1"><strong>Power Flow Model</strong></Typography>
            </Box>
            <List dense>
              <AssumptionItem>Simulates realistic AC power flow, not just simple connectivity.</AssumptionItem>
              <AssumptionItem>Assumes a resilient network that attempts to reroute power during outages.</AssumptionItem>
              <AssumptionItem>Power line capacity is estimated based on the underlying road hierarchy.</AssumptionItem>
              <AssumptionItem>All customer demand is scaled to a single, system-wide peak load for a worst-case analysis.</AssumptionItem>
              <AssumptionItem>Components fail and are disconnected when their load exceeds 110% capacity.</AssumptionItem>
              <AssumptionItem>Automatically disconnects customers (load shedding) to prevent a total grid collapse.</AssumptionItem>
              <AssumptionItem>The model only considers local supply, not electricity supply from power plants located outside the municipal boundaries.</AssumptionItem>
            </List>
          </Paper>
        </Grid>

        {/* Water Model Assumptions */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <WaterDamage color="info" />
              <Typography variant="subtitle1"><strong>Water Flow Model</strong></Typography>
            </Box>
            <List dense>
              <AssumptionItem>Simulates pressure-driven hydraulics; low pressure directly results in reduced water supply.</AssumptionItem>
              <AssumptionItem>A service failure is triggered if water pressure drops below a critical threshold (5 psi).</AssumptionItem>
              <AssumptionItem>Pumping stations have a finite, realistic capacity and are essential for water distribution.</AssumptionItem>
              <AssumptionItem>Disrupting a water reservoir is modeled as a failure of its main pump.</AssumptionItem>
              <AssumptionItem>Pipe diameters and capacity are estimated based on the road hierarchy they follow.</AssumptionItem>
              <AssumptionItem>The model uses a standard "peaking factor" to simulate demand during a typical high-usage period.</AssumptionItem>
              <AssumptionItem>The model only considers local supply, not water delivery from water treatment plants located outside the municipal boundaries.</AssumptionItem>
            </List>
          </Paper>
        </Grid>

        {/* Transport Model Assumptions */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <TravelExplore color="success" />
              <Typography variant="subtitle1"><strong>Transport-Based Models</strong></Typography>
            </Box>
            <List dense>
              <AssumptionItem>Calculates the most mathematically efficient path for service delivery (Min-Cost Flow).</AssumptionItem>
              <AssumptionItem>Does not simulate traffic congestion, driver behavior, or other real-world transport delays.</AssumptionItem>
              <AssumptionItem>Road network links are assumed to have unlimited capacity unless they are directly disrupted by a hazard.</AssumptionItem>
              <AssumptionItem>The "cost" of using a road is based solely on its physical length.</AssumptionItem>
              <AssumptionItem>Models the accessibility of a service, not the complex logistics of its internal operations.</AssumptionItem>
              <AssumptionItem>Unmet demand represents populations that cannot be reached efficiently, indicating severe service disruption.</AssumptionItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}